/**
 * Runtime validation for dynamic schema fields.
 *
 * Each field from the API's `schema_definition` carries a `validators` array
 * (e.g. `["required", "nospace"]`).  This module interprets those validators
 * and returns human-readable error messages that can be shown inline.
 */

export type FieldValidators = string[]

/**
 * Validate a single field value against its validator list.
 *
 * @param value     Current field value
 * @param validators  Array of validator names from the schema definition
 * @param label     Human-readable field label (used in error messages)
 * @returns The first error message, or `null` when valid
 */
export function validateField(
    value: string,
    validators: FieldValidators | undefined,
    label: string,
): string | null {
    if (!validators || validators.length === 0) return null

    for (const validator of validators) {
        const error = runValidator(validator, value, label)
        if (error) return error
    }

    return null
}

/**
 * Validate all fields at once and return a map of field-key → error message.
 * Only fields that fail validation will appear in the result.
 */
export function validateFormFields(
    formValues: Record<string, string>,
    fields: Array<{ key?: string; label?: string; validators?: string[] }>,
): Record<string, string> {
    const errors: Record<string, string> = {}

    for (const field of fields) {
        const key = field.key ?? ''
        if (!key) continue
        const value = formValues[key] ?? ''
        const label = field.label ?? key
        const error = validateField(value, field.validators, label)
        if (error) errors[key] = error
    }

    return errors
}

// ── Individual validator implementations ──────────────────────────────────────

function runValidator(name: string, value: string, label: string): string | null {
    switch (name) {
        case 'required':
            return value.trim() === '' ? `${label} is required` : null

        case 'nospace':
            return /\s/.test(value) ? `${label} must not contain spaces` : null

        case 'email':
            return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? null : `${label} must be a valid email`

        case 'numeric':
            return /^\d+$/.test(value) ? null : `${label} must be a number`

        case 'alpha':
            return /^[a-zA-Z]+$/.test(value) ? null : `${label} must contain only letters`

        case 'alphanumeric':
            return /^[a-zA-Z0-9]+$/.test(value) ? null : `${label} must contain only letters and numbers`

        case 'alphanumeric_underscore':
            return /^[a-zA-Z0-9_]+$/.test(value) ? null : `${label} must contain only letters, numbers, and underscores`

        case 'url':
            try {
                new URL(value)
                return null
            } catch {
                return `${label} must be a valid URL`
            }

        case 'minlength':
            // Parameterised validators like "minlength:3" are handled below
            return null

        default: {
            // Handle parameterised validators: "minlength:N", "maxlength:N", "pattern:regex"
            if (name.startsWith('minlength:')) {
                const min = Number(name.split(':')[1])
                if (Number.isFinite(min) && value.length < min) {
                    return `${label} must be at least ${min} characters`
                }
                return null
            }
            if (name.startsWith('maxlength:')) {
                const max = Number(name.split(':')[1])
                if (Number.isFinite(max) && value.length > max) {
                    return `${label} must be at most ${max} characters`
                }
                return null
            }
            if (name.startsWith('pattern:')) {
                const pattern = name.slice(name.indexOf(':') + 1)
                try {
                    if (!new RegExp(pattern).test(value)) {
                        return `${label} format is invalid`
                    }
                } catch {
                    // invalid regex – skip
                }
                return null
            }
            // Unknown validator – ignore silently
            return null
        }
    }
}
